import { PhoneOff, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { PhoneSystemHealth } from '@/app/providers/phone-system-provider';

function copy(health: PhoneSystemHealth) {
  if (health.restart_running) {
    return {
      title: 'Phone system restarting',
      detail: 'Incoming calls can use the fallback voicemail while service returns.',
    };
  }
  if (!health.restart_enabled) {
    return {
      title: 'Phone system unavailable',
      detail: 'Automatic recovery is paused. Contact your phone system administrator.',
    };
  }
  if (health.last_restart_at) {
    return {
      title: 'Phone system unavailable',
      detail: 'An automatic restart was attempted. Incoming calls can use the fallback voicemail.',
    };
  }
  return {
    title: 'Phone system unavailable',
    detail: 'Automatic recovery is checking the service. Incoming calls can use the fallback voicemail.',
  };
}

export function PhoneSystemNotice({
  health,
  fullScreen = false,
  onRetry,
}: {
  health: PhoneSystemHealth;
  fullScreen?: boolean;
  onRetry?: () => void;
}) {
  const message = copy(health);

  if (fullScreen) {
    return (
      <div className="flex h-full items-center justify-center px-5">
        <section role="status" aria-live="polite" className="w-full max-w-md text-center">
          <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-warn-soft text-warn">
            <PhoneOff className="size-6" aria-hidden />
          </span>
          <h1 className="mt-5 text-lg font-semibold text-ink">{message.title}</h1>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-ink-3">{message.detail}</p>
          <p className="mt-2 text-xs text-ink-4">This page will reconnect when the phone system responds.</p>
          {onRetry ? (
            <Button variant="secondary" size="sm" onClick={onRetry} className="mt-6">
              <RefreshCw />
              Try again
            </Button>
          ) : null}
        </section>
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" className="border-b border-warn/30 bg-warn-soft px-3 py-2 sm:px-4">
      <div className="flex items-start gap-2.5">
        <PhoneOff className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
        <div className="min-w-0 text-xs leading-5">
          <span className="font-semibold text-ink">{message.title}. </span>
          <span className="text-ink-2">{message.detail}</span>
        </div>
      </div>
    </div>
  );
}
